import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PhoneCarousel = ({ imgs, alt, glow = "rgba(34,166,192,0.35)", className = "", testId, contain = false }) => {
    const [index, setIndex] = useState(0);
    const [dir, setDir] = useState(1);
    const [ratios, setRatios] = useState({});
    const many = imgs.length > 1;

    useEffect(() => {
        imgs.forEach((src) => {
            const im = new Image();
            im.onload = () => setRatios((m) => (m[src] !== undefined ? m : { ...m, [src]: im.naturalWidth / im.naturalHeight }));
            im.src = src;
        });
    }, [imgs]);

    const go = (d) => {
        setDir(d);
        setIndex((p) => (p + d + imgs.length) % imgs.length);
    };

    const ratio = ratios[imgs[index]];
    const isFullScreenshot = ratio === undefined ? true : ratio < 0.62;

    const arrows = many ? (
        <>
            <button
                onClick={() => go(-1)}
                className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/85 backdrop-blur-md transition-colors duration-300 hover:bg-black/70"
                data-testid={`${testId}-prev`}
                aria-label="Previous screenshot"
            >
                <ChevronLeft size={18} />
            </button>
            <button
                onClick={() => go(1)}
                className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/85 backdrop-blur-md transition-colors duration-300 hover:bg-black/70"
                data-testid={`${testId}-next`}
                aria-label="Next screenshot"
            >
                <ChevronRight size={18} />
            </button>
        </>
    ) : null;

    const animatedImg = (extraCls) => (
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.img
                key={imgs[index]}
                src={imgs[index]}
                alt={alt}
                loading="lazy"
                custom={dir}
                drag={many ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.15}
                onDragEnd={(e, info) => {
                    if (info.offset.x < -60) go(1);
                    else if (info.offset.x > 60) go(-1);
                }}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -60 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className={extraCls}
                data-testid={`${testId}-image`}
            />
        </AnimatePresence>
    );

    return (
        <div className={`relative ${className}`} data-testid={testId}>
            <div
                aria-hidden="true"
                className="absolute -inset-12 rounded-full opacity-60 blur-3xl"
                style={{ background: `radial-gradient(circle, ${glow}, transparent 70%)` }}
            />
            {isFullScreenshot ? (
                <div className="relative w-[240px] rounded-[2.6rem] border border-white/10 bg-[#0D4159] p-[9px] shadow-[0_40px_90px_rgba(0,0,0,0.6)] md:w-[290px]">
                    <div className="relative overflow-hidden rounded-[2rem] bg-black" style={{ aspectRatio: "1320 / 2716" }}>
                        {animatedImg(`absolute inset-0 h-full w-full ${contain ? "object-contain" : "object-cover"}`)}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.03] to-white/[0.1]" />
                        {arrows}
                    </div>
                </div>
            ) : (
                <div className="relative w-[300px] md:w-[440px]">
                    <div
                        className="relative overflow-hidden rounded-3xl border border-white/10 bg-black shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
                        style={{ aspectRatio: ratio ? String(ratio) : "4 / 3" }}
                        data-testid={`${testId}-plain`}
                    >
                        {animatedImg("absolute inset-0 h-full w-full object-contain")}
                        {arrows}
                    </div>
                </div>
            )}
            {many && (
                <div className="mt-6 flex items-center justify-center gap-2" data-testid={`${testId}-dots`}>
                    {imgs.map((_, d) => (
                        <button
                            key={d}
                            onClick={() => {
                                setDir(d > index ? 1 : -1);
                                setIndex(d);
                            }}
                            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
                                d === index ? "w-7 bg-[#0D9488]" : "w-1.5 bg-[#0F172A]/15 hover:bg-[#0F172A]/30"
                            }`}
                            data-testid={`${testId}-dot-${d}`}
                            aria-label={`Screenshot ${d + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
